import React, { useCallback, useEffect } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import getConfig from 'next/config';

import { send, listen, unlisten } from '@atc/cai-event-bus';

import {
    ConnectedMyWalletModal,
    VrsServiceClient,
    WalletManagerContainer,
    ownerDetailsFetcher,
} from 'reaxl-wallet';

import {
    WALLET_VALIDATE_DEALS_BY_VIN_REQUEST,
    WALLET_VALIDATE_DEALS_BY_VIN_COMPLETED,
} from 'wallet-manager';

import { sendClick } from 'reaxl-analytics';

import getDrDealDetails, { resetDealNotifications } from '@/utilities/getDrDealDetails';

import {
    dealsDuck,
    interestRateDuck,
    inventoryDuck,
    paymentsDuck,
    userDuck,
} from '@/ducks';

import {
    fetchIco,
    fetchKBBTradeIns as tradeInFetcher,
    fetchCreditDecision as creditApplicationsFetcher,
    fetchCreditVerification as creditVerificationFetcher,
    fetchPaymentCalculation as paymentFetcher,
} from '@/fetchers';

import fetchVehicleDataService from '@/fetchers/fetchVehicleDataService';

import { handleBrowseByBudgetPriceSubmit } from '@/actions/heroSearchActions';

const vrsServices = new VrsServiceClient({
    vrsFetcher: fetchVehicleDataService,
});

function MyWalletContainer() {
    const dispatch = useDispatch();
    const pageDetails = useSelector((state) => state?.pageDetails);

    let pageListingType = null;

    switch (pageDetails.pageType) {
        case 'slp_new':
            pageListingType = 'NEW';
            break;
        case 'slp_used':
            pageListingType = 'USED';
            break;
        default:
            pageListingType = null;
            break;
    }

    const { publicRuntimeConfig } = getConfig();
    const env = publicRuntimeConfig.environment;

    const handleValidSubmitCallback = useCallback((maxPrice, walletPaymentOption) => {
        const price = maxPrice || 25000;
        const listingType = walletPaymentOption === 'lease' ? 'NEW' : pageListingType;

        dispatch(paymentsDuck.creators.updatePaymentsInfo({ budget: price }));

        sendClick('myWalletSearchByBudgetClick', null);
        handleBrowseByBudgetPriceSubmit({
            price,
            pageListingType: listingType,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const walletProps = {
        id: 'myWalletModal',
        env, // Used to configure the SDP in nonprod environments
        pageName: 'home',
        paymentsDuck,
        dealsDuck,
        userDuck,
        interestRateDuck,
        inventoryDuck,
        tradeInFetcher,
        IcoFetcher: fetchIco,
        vrsServicesFetcher: vrsServices,
        creditApplicationsFetcher,
        creditVerificationFetcher,
        drDealDetailsFetcher: getDrDealDetails,
        resetDealNotifications,
        paymentFetcher,
        onSearchByBudget: ({ maxPrice, walletPaymentOption }) => {
            handleValidSubmitCallback(maxPrice, walletPaymentOption);
        },
    };

    const appFetchers = {
        ownerDetailsFetcher: ownerDetailsFetcher({
            hostname: '/collections/lcServices',
        }),
    };

    const validateDealsByVin = (vins = []) => {
        const vinsWithVdpHref = vins.reduce((acc, vin, key) => {
            const vinObject = { vin };

            acc.push(vinObject);

            return acc;
        }, []);
        send(WALLET_VALIDATE_DEALS_BY_VIN_COMPLETED, vinsWithVdpHref);
    };

    useEffect(() => {
        listen(WALLET_VALIDATE_DEALS_BY_VIN_REQUEST, validateDealsByVin);
        return () => {
            unlisten(WALLET_VALIDATE_DEALS_BY_VIN_REQUEST, validateDealsByVin);
        };
    }, []);

    return (
        <>
            <WalletManagerContainer
                paymentsDuck={paymentsDuck}
                env={publicRuntimeConfig?.environment === 'prod' ? 'prod' : 'staging'}
                fetchers={appFetchers}
            />
            <ConnectedMyWalletModal
                {...walletProps}
            />
        </>
    );
}

export default MyWalletContainer;
